import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Code,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
  Spinner,
  Text,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import { sortBy } from 'lodash';
import moment from 'moment';
import React from 'react';

import { ChannelCardChannelFragment } from 'src/app/graph/graphSettingsPage/reportingPage/channelCard/ChannelCard';
import { variantRouteConfig } from 'src/app/graph/routes';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const allTimezonesQuery = gql<
  GraphQLTypes.AllTimezonesQuery,
  GraphQLTypes.AllTimezonesQueryVariables
>`
  query AllTimezonesQuery {
    allTimezoneOffsets {
      zoneID
      minutesOffsetFromUTC
    }
  }
`;

export const upsertSummaryMutation = gql`
  mutation UpsertSummaryMutation(
    $graphId: ID!
    $summaryId: ID
    $channelId: ID
    $enabled: Boolean
    $timezone: String
    $variant: String
  ) {
    service(id: $graphId) {
      upsertScheduledSummary(
        id: $summaryId
        channelID: $channelId
        enabled: $enabled
        timezone: $timezone
        variant: $variant
      ) {
        id
        channels {
          id
          ...ChannelCardChannelFragment
        }
      }
    }
  }
  ${ChannelCardChannelFragment}
`;

interface FormValues {
  timezone: string;
}

interface Props {
  isOpen: boolean;
  state: Readonly<CreateScheduledSummaryState> | null;
  onSubscriptionCreated: (newSubscriptionId: string) => void;
  onPrevious: () => void;
  onClose: () => void;
}

export interface CreateScheduledSummaryState {
  targetChannel?: GraphQLTypes.AddNotificationWizardChannelFragment;
  variant?: string;
  newSubscriptionId?: string;
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
}

function sortByCurrentTZThenOffsetThenAlpha(
  tzs: GraphQLTypes.AllTimezonesQuery_allTimezoneOffsets[],
) {
  const currentOffset = moment().utcOffset();
  return sortBy(tzs, [
    (tz) => (tz.minutesOffsetFromUTC === currentOffset ? -1 : 1),
    (tz) => tz.minutesOffsetFromUTC,
    (tz) => tz.zoneID.toUpperCase(),
  ]);
}

export const CreateScheduledSummaryModal = ({
  isOpen,
  state,
  onSubscriptionCreated,
  onPrevious,
  onClose,
}: Props) => {
  const { graphId } = useRouteParams(variantRouteConfig);
  const {
    data: timezonesData,
    loading: timezonesLoading,
    error: timezonesError,
  } = useQuery(allTimezonesQuery);

  const [upsertSummary, { error: upsertSummaryError }] = useMutation<
    GraphQLTypes.UpsertSummaryMutation,
    GraphQLTypes.UpsertSummaryMutationVariables
  >(upsertSummaryMutation);

  const sortedTimezones = sortByCurrentTZThenOffsetThenAlpha(
    /**
     * Filter out "weird" Etc/XXX timezones b/c they are confusing.
     * POSIX compatibility requires that the offsets are inverted. Therefore,
     * Etc/GMT-X will have an offset of `+X` and Etc/GMT+X will have an offset
     * of `-X`. This is a result of IANA's [Time Zone Database]
     * (https://github.com/eggert/tz/blob/0c64fd7138472ae6819c8f5535d4cd163ee3e98d/etcetera#L36-L42).
     */
    timezonesData?.allTimezoneOffsets.filter(
      (tz) => !tz.zoneID.startsWith('Etc/'),
    ) ?? [],
  );

  const { values, errors, touched, handleChange, handleSubmit, isSubmitting } =
    useFormik<FormValues>({
      initialValues: {
        timezone: sortedTimezones[0]?.zoneID ?? '',
      },
      enableReinitialize: true,
      async onSubmit(formValues) {
        const { timezone } = formValues;
        const subscription = await upsertSummary({
          variables: {
            graphId,
            enabled: true,
            channelId: state?.targetChannel?.id,
            timezone,
            variant: state?.variant,
          },
        });
        const newSubscriptionId =
          subscription.data?.service?.upsertScheduledSummary?.id;
        if (subscription && newSubscriptionId) {
          onSubscriptionCreated(newSubscriptionId);
        }
      },
    });

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Configure daily report</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <FormControl
              id="timezone"
              isInvalid={!!errors.timezone && !!touched.timezone}
            >
              <div>
                <FormLabel className="mb-1">Timezone</FormLabel>
                <Text>
                  Reports will be delivered at <Code>9:00 AM</Code> in the
                  selected timezone.
                </Text>
              </div>

              {timezonesLoading ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <Select
                    name="timezone"
                    value={values.timezone}
                    onChange={handleChange}
                    isDisabled={isSubmitting}
                  >
                    {sortedTimezones.map((tz) => (
                      <option value={tz.zoneID} key={tz.zoneID}>
                        {tz.zoneID} (UTC {tz.minutesOffsetFromUTC >= 0 && '+'}
                        {tz.minutesOffsetFromUTC / 60})
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.timezone}</FormErrorMessage>
                </>
              )}
            </FormControl>

            {(timezonesError || upsertSummaryError) && (
              <ErrorMessage>
                Something went wrong, please try again.
              </ErrorMessage>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              type="button"
              isDisabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                isDisabled={isSubmitting}
                onClick={onPrevious}
              >
                Previous
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Next"
                type="submit"
              >
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
